import { useCallback, useEffect, useRef, useState } from 'react'
import { errorMessage } from '../../chat/api'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { useToast } from '../../library/useToast'
import { useCrmRequest } from '../CrmRequest'
import { ReferralsSide } from '../statistics/Aside'
import { CustomRange, PeriodBar } from '../statistics/PeriodBar'
import { ReferralsMain } from '../statistics/StatementsViews'
import { StatsContext, useStatsT, type StatsCtx } from '../statistics/statsContext'
import { Pills } from '../statistics/StatsParts'
import { computeRange, currentOfUrl, customFromRange, DEFAULT_PILLS, periodKey, TABS, type Month, type Period, type Pills as PillState, type Query, type Range, type Raw } from '../statistics/statisticsData'
import { StatsStore } from '../statistics/statsStore'
import type { MorePageProps } from './morePage'
import { referralsQuery, referralsTab } from './referrals'
import '../../library/library.css'
import '../ofPages.css'
import '../statements/statements.css'
import '../statistics/statistics.css'

const STATEMENTS = TABS.find((x) => x.id === 'statements')!

/**
 * Referrals (More → Referrals): onlyfans.com/my/statistics/statements/referrals/
 * <earnings|creators|users> — OnlyFans opens its Statistics page on the
 * Statements → Referrals tab, so this is that view of our Statistics copy (its
 * chart, payout requests, referred creators/users, balance card and Earnings
 * summary) read through the same POST /v1/statistics/of. Its pill is the
 * page's `tab=` query; every other Statistics tab opens the Statistics page.
 */
export function ReferralsPage({ query, onOpenPage, onOpenChat, mobileNav }: MorePageProps) {
  const { t } = useStatsT()
  const request = useCrmRequest()
  const mobile = useIsNarrow()
  const { say, node: toast } = useToast()
  const requestRef = useRef(request)
  requestRef.current = request

  const [store] = useState(
    () =>
      new StatsStore(async (path: string, q: Query) => {
        const { status, data } = await requestRef.current('/v1/statistics/of', { local: true, body: { path, query: q } })
        const body = data as Raw | null
        if (status !== 200 || !body || body.ok === false) throw new Error(errorMessage(body, `HTTP ${status}`))
        return body.data
      }),
  )
  const [openedAt] = useState(() => new Date())
  const ranges = useRef(new Map<string, Range>())
  const periodGen = useRef(0)
  const [period, setPeriod] = useState<Period>({ key: '30d' })
  const [range, setRange] = useState<Range>(() => {
    const r = computeRange({ key: '30d' }, null, openedAt)
    ranges.current.set('30d', r)
    return r
  })
  const [menuOpen, setMenuOpen] = useState(false)
  const [custom, setCustom] = useState<{ from: string; to: string } | null>(null)
  const [streakMonth, setStreakMonth] = useState<Month>(() => ({ y: openedAt.getFullYear(), m: openedAt.getMonth() }))
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())

  const pill = referralsTab(query)
  const pills: PillState = { ...DEFAULT_PILLS, referrals: pill }
  const setPill = useCallback(
    (group: keyof PillState, value: string) => {
      if (group === 'referrals') onOpenPage('referrals', referralsQuery(value), { replace: true })
    },
    [onOpenPage],
  )

  /** One range per period choice for the page's lifetime (as on Statistics): going back to a period hits the cache. */
  const applyPeriod = async (next: Period) => {
    const gen = ++periodGen.current
    const key = periodKey(next)
    let r = ranges.current.get(key)
    if (!r) {
      let startModel: string | null = null
      if (next.key === 'all') {
        try {
          startModel = String(((await store.read('users/me/start-date-model')) as Raw | null)?.startDate || '') || null
        } catch {
          startModel = null
        }
      }
      if (gen !== periodGen.current) return
      r = computeRange(next, startModel)
      ranges.current.set(key, r)
    }
    setPeriod(next)
    setRange(r)
    setExpanded(new Set())
  }
  const pickPeriod = (key: string) => {
    setMenuOpen(false)
    if (key === 'custom') setCustom(customFromRange(range))
    else void applyPeriod({ key })
  }
  const applyCustom = () => {
    if (!custom || !custom.from || !custom.to || custom.from > custom.to) return
    setCustom(null)
    void applyPeriod({ key: 'custom', from: custom.from, to: custom.to })
  }

  useEffect(() => {
    if (!menuOpen) return
    const close = () => setMenuOpen(false)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [menuOpen])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (custom) setCustom(null)
      else if (menuOpen) setMenuOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [custom, menuOpen])

  const ctx: StatsCtx = {
    store,
    request,
    range,
    pills,
    setPill,
    expanded,
    toggleExpanded: (id) =>
      setExpanded((all) => {
        const next = new Set(all)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      }),
    countriesAll: false,
    showAllCountries: () => {},
    streakMonth,
    setStreakMonth,
    openChat: (fanId) => onOpenChat(fanId),
    // Referrals has no withdrawal: referral earnings are paid out monthly by OnlyFans.
    openWithdrawal: () => {},
    canWritePayouts: false,
    say,
    openedAt,
  }

  const head = (
    <header className="lib-main-head ofx-head">
      <button type="button" className="lib-icon-btn" aria-label={t('back')} title={t('back')} onClick={() => onOpenPage('more')}>
        <FeatherIcon name="arrow-left" size={19} />
      </button>
      <h1 className="lib-main-title ofx-title">{t('title')}</h1>
      <span className="lib-spacer" />
      <a className="lib-icon-btn" href={currentOfUrl('statements', 'referrals', pills)} target="_blank" rel="noopener noreferrer" aria-label={t('openOnOf')} title={t('openOnOf')}>
        <FeatherIcon name="external-link" size={18} />
      </a>
    </header>
  )
  // The other Statistics tabs are the Statistics page's own.
  const tabs = (
    <nav className="ofx-tabs" role="tablist" aria-label={t('sections')}>
      {TABS.map((x) => (
        <button
          key={x.id}
          type="button"
          role="tab"
          aria-selected={x.id === 'statements'}
          className={x.id === 'statements' ? 'is-on' : ''}
          onClick={() => x.id !== 'statements' && onOpenPage('statistics')}
        >
          {t(`tab.${x.id}`)}
        </button>
      ))}
    </nav>
  )
  const content = (
    <>
      <div className="ofx-subtabs">
        <Pills
          items={STATEMENTS.subs.map((s) => [s.id, s.label] as const)}
          active="referrals"
          onPick={(id) => id !== 'referrals' && onOpenPage('statistics')}
          label={t('tab.statements')}
        />
      </div>
      <PeriodBar period={period} range={range} withTz={false} open={menuOpen} onToggle={() => setMenuOpen((o) => !o)} onPick={pickPeriod} />
      <div className="ofx-content">
        <ReferralsMain />
      </div>
    </>
  )
  const aside = (
    <aside className="ofx-aside" aria-label={t('summaryLabel')}>
      <ReferralsSide />
    </aside>
  )

  return (
    <StatsContext.Provider value={ctx}>
      <div className={`lib ofp ofx ${mobile ? 'lib--m ofx--m' : ''}`}>
        {mobile ? (
          <>
            {head}
            {tabs}
            <div className="ofx-scroll">
              {content}
              {aside}
            </div>
            {mobileNav}
          </>
        ) : (
          <>
            <section className="lib-main ofx-main">
              {head}
              {tabs}
              <div className="ofx-scroll">{content}</div>
            </section>
            {aside}
          </>
        )}
        {custom && (
          <CustomRange
            value={custom}
            onChange={(which, day) => setCustom((c) => c && { ...c, [which]: day })}
            onCancel={() => setCustom(null)}
            onApply={applyCustom}
          />
        )}
        {toast}
      </div>
    </StatsContext.Provider>
  )
}
